import React from 'react';
import { Box, Paper, Chip, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { format, parseISO, isWithinInterval, addDays } from 'date-fns';
import { ATTENDANCE_COLORS, AttendanceDayKind } from '../../constants/attendanceColors';

// One synthesized calendar day for an attendance grid — unlike a raw attendance record,
// there's always exactly one of these per date in the selected range (capped at today), whether
// or not there's an actual DB record for that day. Shared between the admin's per-employee view
// (AdminAttendanceLeave) and an employee's own view (AttendanceTracking) so both render the same
// day-by-day grid and only the role-specific action (Reject vs. Regularize, etc.) differs.
export interface AttendanceDayRow {
  date: Date;
  dateStr: string;
  kind: AttendanceDayKind;
  label: string;
  checkIn?: string;
  checkOut?: string;
  remarks?: string;
  record?: any; // the real attendance record, when kind is 'present' or 'halfday'
}

export const SUMMARY_LABELS: Record<AttendanceDayKind, string> = {
  present: 'Full Day', halfday: 'Half Day', leave: 'On Leave', holiday: 'Holiday', weekend: 'Weekly Off', missing: 'Missing',
};

/**
 * Builds one row per calendar date in `range` (capped at today — a future date is just empty,
 * not worth a row), merging attendance records with approved leave and company holidays, so
 * every past/today date shows up (Sunday as the weekly off, Mon-Sat with no record/leave/holiday
 * as a missing day) instead of only the dates that happen to have a DB row.
 *
 * `records` and `approvedLeaves` must already be scoped to the one employee this grid is for.
 */
export function buildAttendanceDayRows(
  records: any[],
  approvedLeaves: any[],
  holidays: any[],
  range: { start: Date; end: Date }
): AttendanceDayRow[] {
  const today = new Date();
  const end = range.end > today ? today : range.end;
  const recordsByDate = new Map(records.map(r => [format(parseISO(r.date), 'yyyy-MM-dd'), r]));
  const holidayDates = new Map(
    holidays.filter(h => h.isActive).map(h => [format(parseISO(h.date), 'yyyy-MM-dd'), h.name])
  );

  const rows: AttendanceDayRow[] = [];
  if (range.start > end) return rows; // the whole range is in the future — nothing to show yet
  let cursor = new Date(range.start);
  while (cursor <= end) {
    const date = cursor; // stable per-iteration reference for the closure below
    const dateStr = format(date, 'yyyy-MM-dd');
    const record = recordsByDate.get(dateStr);
    const leave = approvedLeaves.find(l => isWithinInterval(date, { start: parseISO(l.startDate), end: parseISO(l.endDate) }));
    const holidayName = holidayDates.get(dateStr);

    if (record) {
      const isHalfDay = record.status === 'Half Day';
      rows.push({
        date: new Date(date), dateStr,
        kind: isHalfDay ? 'halfday' : 'present',
        label: isHalfDay ? 'Half Day' : record.status === 'Present' ? 'Full Day' : record.status,
        checkIn: record.checkInTime, checkOut: record.checkOutTime, remarks: record.remarks, record,
      });
    } else if (leave) {
      rows.push({ date: new Date(date), dateStr, kind: 'leave', label: `On Leave — ${leave.leaveType}` });
    } else if (holidayName) {
      rows.push({ date: new Date(date), dateStr, kind: 'holiday', label: `Holiday — ${holidayName}` });
    } else if (date.getDay() === 0) {
      rows.push({ date: new Date(date), dateStr, kind: 'weekend', label: 'Weekly Off' });
    } else {
      rows.push({ date: new Date(date), dateStr, kind: 'missing', label: 'Missing' });
    }
    cursor = addDays(cursor, 1);
  }
  return rows.reverse(); // most recent first
}

/**
 * The shared day-by-day attendance grid: a small summary strip (counts per status) above a
 * fixed-height, sticky-header, scrollable table — same design for every role. `renderAction` is
 * the one role-specific slot (e.g. Admin passes a Reject button, an employee might pass a
 * Regularize shortcut on "Missing" rows, or omit it entirely).
 */
export const AttendanceDayGrid: React.FC<{
  rows: AttendanceDayRow[];
  renderAction?: (row: AttendanceDayRow) => React.ReactNode;
}> = ({ rows, renderAction }) => {
  const summary = rows.reduce((acc, r) => {
    acc[r.kind] = (acc[r.kind] ?? 0) + 1;
    return acc;
  }, {} as Partial<Record<AttendanceDayKind, number>>);

  return (
    <Box>
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
        {(Object.keys(SUMMARY_LABELS) as AttendanceDayKind[]).map((kind) => (
          <Chip
            key={kind}
            size="small"
            label={`${SUMMARY_LABELS[kind]}: ${summary[kind] ?? 0}`}
            sx={{ bgcolor: ATTENDANCE_COLORS[kind].bg, color: ATTENDANCE_COLORS[kind].text, fontWeight: 600 }}
          />
        ))}
      </Stack>

      {/* Fixed-height scroll area so a full month doesn't blow out the page scroll. */}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '8px', maxHeight: 420 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Check In</TableCell>
              <TableCell>Check Out</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Remarks</TableCell>
              {renderAction && <TableCell>Action</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={renderAction ? 6 : 5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  No dates in range yet.
                </TableCell>
              </TableRow>
            ) : rows.map((row) => {
              const style = ATTENDANCE_COLORS[row.kind];
              return (
                <TableRow key={row.dateStr} sx={{ bgcolor: style.bg }}>
                  <TableCell>{format(row.date, 'EEE, MMM dd, yyyy')}</TableCell>
                  <TableCell>{row.checkIn || '-'}</TableCell>
                  <TableCell>{row.checkOut || '-'}</TableCell>
                  <TableCell>
                    <Chip label={row.label} size="small" sx={{ bgcolor: style.dot, color: 'white', fontWeight: 600 }} />
                  </TableCell>
                  <TableCell>{row.remarks || '-'}</TableCell>
                  {renderAction && <TableCell>{renderAction(row)}</TableCell>}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
